import React, { memo, useCallback, useState, useMemo, useRef } from 'react';
import { SectionList } from '@web/web5';
import { Button, Spacer, Icon } from 'hoi-poi-ui';

import {
    getAutomationsLog,
    getAutomationLog,
    getAutomationsLogCount,
} from 'services/AutomationsService';

import Context from 'managers/Context';

import * as ENTITIES from 'constants/Entities';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { getUserSfmUrl } from 'utils/getUrl';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { AUTOMATION_TRIGGERS } from './utils';

import AutomationsListEmptyView from './components/AutomationsListEmptyView';
import { BadgeCell, TextPopoverCell, IconCell } from 'components/TableCells';

import './styles.scss';

const AutomationsLog = memo(({ openBuilder }) => {
    const [error, setError] = useState(false);
    const totalList = useRef([]);
    const tableRef = useRef(null);

    const getRowNodeId = useCallback((data) => String(data.id), []);

    const refresh = useCallback(() => {
        totalList.current = [];
        tableRef.current?.refresh({ resetOffset: true });
    }, []);

    const get = useCallback((pageSize, startRow) => {
        if (startRow > totalList.current.length) {
            return Promise.reject();
        }

        return getAutomationsLog({ count: pageSize, offset: startRow })
            .then((data) => {
                // Result processing
                function resultProcessing(d) {
                    let result = {};
                    let resultDetails = '';
                    switch (d?.result) {
                        case 'success':
                            result = {
                                text: getLiteral('label_automation_result_success'),
                                type: 'semanticPositive',
                            };
                            resultDetails = getLiteralWithParameters(
                                'label_automations_result_details_sucess',
                                [''],
                            );
                            break;
                        case 'filtered':
                            result = {
                                text: getLiteral('label_automation_result_filtered'),
                                type: 'default',
                            };
                            resultDetails = getLiteralWithParameters(
                                'label_automations_result_details_filtered',
                                [''],
                            );
                            break;
                        case 'error':
                            result = {
                                text: getLiteral('label_automation_result_error'),
                                type: 'semanticNegative',
                            };
                            resultDetails = getLiteralWithParameters(
                                'label_automations_result_details_error',
                                ['', d?.error],
                            );
                            break;
                        default:
                            break;
                    }

                    return { result, resultDetails };
                }

                const entities = data.map((d) => {
                    const { src, placeholder } = getSrcUserCircleAvatar(d?.triggerUser?.id);
                    const entity = Object.values(ENTITIES).find((e) => e.trueName === d.entity);
                    let name = getLiteral(AUTOMATION_TRIGGERS[d.action]?.name);
                    if (d.withCreation) {
                        name += ` (${getLiteral('label_automation_event_creation_included')})`;
                    }

                    return {
                        ...d,
                        automation: d?.automation?.description,
                        ...resultProcessing(d),
                        triggeringUser: d?.triggerUser?.description,
                        triggeringUserId: d?.triggerUser?.id,
                        triggeringUserSrc: src,
                        triggeringUserPlaceholder: placeholder,
                        triggerEventIcon: AUTOMATION_TRIGGERS[d.action]?.icon,
                        triggerEvent: name,
                        triggerEntityIcon: d.entity,
                        triggerEntity: entity?.labels?.singular
                            ? getLiteral(entity?.labels?.singular)
                            : d.entity,
                        triggeringRecord:
                            d?.triggerRecord?.description || getLiteral('label_no_data'),
                        triggeringRecordURL: entity?.route
                            ? `/#${entity?.route}/${d?.triggerRecord?.id}`
                            : '',
                    };
                });

                totalList.current.push(...entities);

                return {
                    entities,
                };
            })
            .catch((e) => {
                console.error(e);
                setError(true);
                errorToast({
                    title: getLiteral('error_error'),
                    text: getLiteral('error_generalerror'),
                });
            });
    }, []);

    const getTotal = useCallback(() => {
        return getAutomationsLogCount({})
            .then((data) => {
                return data?.total || 0;
            })
            .catch((e) => {
                console.error(e);
                setError(true);
                errorToast({
                    title: getLiteral('error_error'),
                    text: getLiteral('error_generalerror'),
                });
            });
    }, []);

    const downloadLog = useCallback((row) => {
        getAutomationLog(row.id)
            .then((data) => {
                const blob = new Blob([JSON.stringify(data.trace, null, 2)], {
                    type: 'application/json',
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `automation-log-${row.id}.json`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((e) => {
                console.error(e);
                errorToast({
                    title: getLiteral('error_error'),
                    text: getLiteral('error_generalerror'),
                });
            });
    }, []);

    const tableProps = useMemo(
        () => ({
            components: {
                BadgeCell,
                TextPopoverCell,
                IconCell,
            },
            columnDefs: [
                {
                    colId: 'id',
                    field: 'id',
                    headerName: getLiteral('label_run_id'),
                    headerComponent: 'headerTextCell',
                    headerComponentParams: {
                        showTotal: true,
                    },
                    cellRenderer: 'textCell',
                    cellRendererParams: {
                        bold: true,
                    },
                    width: 400,
                    suppressSizeToFit: true,
                    pinned: 'left',
                },
                {
                    colId: 'date',
                    field: 'date',
                    headerName: getLiteral('cfm_label_execution_date'),
                    cellRenderer: 'dateCell',
                    cellRendererParams: {
                        locale: () => Context?.config?.userData?.locale,
                        inputFormat: "yyyy-MM-dd'T'HH:mm:ss",
                        outputFormat: 'P p',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'automation',
                    field: 'automation',
                    headerName: getLiteral('label_automation'),
                    cellRenderer: 'textCell',
                    minWidth: 250,
                },
                {
                    colId: 'result',
                    field: 'result',
                    headerName: getLiteral('cfm_label_result'),
                    cellRenderer: 'BadgeCell',
                    minWidth: 250,
                },
                {
                    colId: 'resultDetails',
                    field: 'resultDetails',
                    headerName: getLiteral('label_result_details'),
                    cellRenderer: 'TextPopoverCell',
                    minWidth: 350,
                },
                {
                    colId: 'triggeringUser',
                    field: 'triggeringUser',
                    headerName: getLiteral('label_triggering_user'),
                    cellRenderer: 'avatarCell',
                    cellRendererParams: {
                        getUrl: (data) => {
                            const id = data.triggeringUserId;
                            if (!id) return;
                            return getUserSfmUrl(id) || '';
                        },
                        otherFields: {
                            src: 'triggeringUserSrc',
                            placeholder: 'triggeringUserPlaceholder',
                        },
                    },
                    minWidth: 250,
                },
                {
                    colId: 'triggerEntity',
                    field: 'triggerEntity',
                    headerName: getLiteral('label_triggering_entity'),
                    cellRenderer: 'IconCell',
                    cellRendererParams: {
                        iconValue: 'triggerEntityIcon',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'triggerEvent',
                    field: 'triggerEvent',
                    headerName: getLiteral('label_triggering_event'),
                    cellRenderer: 'IconCell',
                    cellRendererParams: {
                        iconValue: 'triggerEventIcon',
                    },
                    minWidth: 250,
                },
                {
                    colId: 'triggeringRecord',
                    field: 'triggeringRecord',
                    headerName: getLiteral('label_triggering_record'),
                    cellRenderer: 'avatarCell',
                    cellRendererParams: {
                        getUrl: (data) => {
                            return data.triggeringRecordURL || '';
                        },
                    },
                    minWidth: 250,
                },
            ],
            forceActionsRefresh: true,
            actions: [
                (row) => {
                    return {
                        icon: <Icon name="download" onClick={() => downloadLog(row)} />,
                        tooltip: { content: getLiteral('label_result_details_download') },
                    };
                },
            ],
            onRef: (ref) => {
                tableRef.current = ref;
            },
        }),
        [downloadLog],
    );

    const getErrorView = useCallback(() => {
        return {
            title: getLiteral('error_an_error_occurred'),
            subtitle: getLiteral('error_reload_table'),
            button: (
                <Button type="secondary" onClick={refresh}>
                    {getLiteral('action_reload_table')}
                </Button>
            ),
        };
    }, [refresh]);

    const onCreate = useCallback(() => {
        openBuilder();
    }, [openBuilder]);

    const emptyViewProps = useMemo(() => {
        return {
            onCreate: openBuilder,
        };
    }, [openBuilder]);

    return (
        <>
            <div className="fm-automations-settings__toolbar">
                <span></span>
                <Button onClick={onCreate}>{getLiteral('action_create')}</Button>
            </div>
            <Spacer y={2} />
            <SectionList
                id="automations-log"
                {...tableProps}
                useLoaderPlaceholder
                hasError={error}
                getRowNodeId={getRowNodeId}
                errorViewProps={getErrorView}
                emptyViewProps={emptyViewProps}
                customEmptyViewComponent={AutomationsListEmptyView}
                //Infinite scrolling props
                forceActionsRefresh
                initTable={get}
                getTotal={getTotal}
                pageSize={50}
                useLazyLoad
                initOnReady
            />
        </>
    );
});

export default AutomationsLog;
